/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { RightsContext } from 'providers/RightsContext';
import { withParams } from 'providers/ParamsConsumer';
import {
  initAssetData,
  loadAssetData,
  changeAssetProperty,
  cancelAssetChanges,
  loadContactInfo,
  loadPropertyDetails,
  loadPropertiesHistory,
  saveAsset,
  duplicateAsset,
  chassisNumberSave,
  cancelChassisNumberSave,
  splitAsset,
  loadSecondHandProperties
} from 'features/offer/edit/assets/asset/actionCreators';
import { invalidateConfiguratorData } from 'features/asset/configurator/actionCreators';
import { loadDealers, loadSuppliers } from 'actions/actionCreators/dictionariesActions';
import OfferAssetCard from './OfferAssetCard';
import curry from 'lodash.curry';
import { isChassisNumber } from 'utils/simple-validations/validations';
import { getAssetHierarchyImage, getAssetTypeImage } from 'features/asset/hierarchy/selectors';
import withCustomComponent from 'providers/withCustomComponent';
import { withRights } from 'providers/RightsConsumer';
import { getCurrencies } from 'selectors/dictionariesSelectors';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const OfferAssetContainer = ({
  actions,
  documentId,
  siteId,
  documentDetailId,
  history,
  userInfo,
  asset,
  hierarchy,
  assetTypes,
  assetIsLoaded,
  assetPropertiesAreLoaded,
  assetImage,
  currencies,
  saving,
  duplicating,
  steps,
  activeStep,
  updatingChassisNumber,
  retrievingAssetData,
  maxMonths,
  selfPartnerName,
  isFacilityFrame,
  params,
  rights,
  processing,
  hasWorkingHours,
  defaultValue
}) => {
  const [localChassisNumber, setLocalChassisNumber] = useState('');
  const [localSupplier, setLocalSupplier] = useState({});

  const [localOrderNumber, setLocalOrderNumber] = useState('');
  const [openOptions, setOpenOptions] = useState(false);
  const [showConfigurator, setShowConfigurator] = useState(false);
  const [showPropertiesHistory, setShowPropertiesHistory] = useState(false);
  const [enableConfiguratorButton, setEnableConfiguratorButton] = useState(false);

  const handleOpenOptions = () => setOpenOptions(true);
  const handleCloseOptions = () => setOpenOptions(false);

  const { updateAssetChassisNo, financedAssetWrite } = useContext(RightsContext);
  const { assetHierarchyConfiguration } = params;
  const editRight = !!rights?.financedAssetDealerWrite;
  const { t } = useTranslation();

  const onSaveAssetComplete = (documentId, siteId /* documentDetailId*/) => {
    var step = steps.find(a => a.id === activeStep);
    history.push(step.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId));
  };

  const onDuplicateComplete = (documentId, siteId /* documentDetailId*/) => {
    var step = steps.find(a => a.id === activeStep);
    history.push(step.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId));
    toast(t('OfferEdit.Asset.Duplicated'), 'success');
  };

  const onAssetCanceled = () => {
    history.goBack();
  };

  useEffect(() => {
    actions.initAssetData(documentId, siteId, documentDetailId, isFacilityFrame);
    actions.invalidateConfiguratorData();
    return () => {
      actions.cancelAssetChanges(documentId, siteId);
    };
  }, []);

  useEffect(() => {
    if (assetIsLoaded === false) {
      actions.initAssetData(documentId, siteId, documentDetailId, isFacilityFrame);
    }
  }, [assetIsLoaded]);

  useEffect(() => {
    var itemId =
      asset?.document?.itemId ??
      (asset?.document?.assetTypeId ? assetTypes.find(x => x.assetTypeId === asset.document.assetTypeId)?.itemId : null);
    actions.loadSecondHandProperties(itemId);
  }, [actions, asset?.document?.assetTypeId, asset.document?.itemId, assetTypes]);

  useEffect(() => {
    if (asset && asset.document && asset.document.chassisNumber) {
      setLocalChassisNumber(asset.document.chassisNumber);
    }
  }, [asset]);

  useEffect(() => {
    if (asset && asset.document && asset.document.partner) {
      setLocalSupplier(asset.document.partner.supplier || {});
    }
  }, [asset]);

  const assetTypeId = assetIsLoaded ? asset.document.assetTypeId : null;
  useEffect(() => {
    if (!assetTypeId) return;

    const selectedAssetType = assetTypes.find(x => x.assetTypeId === asset.document.assetTypeId);
    if (!selectedAssetType) return;

    const selectedAssetTypeFromHierachyConfiguration = assetHierarchyConfiguration.find(x => x.hierarchyCode === selectedAssetType.code);
    if (!selectedAssetTypeFromHierachyConfiguration) setEnableConfiguratorButton(false);
    else setEnableConfiguratorButton(selectedAssetTypeFromHierachyConfiguration.enableConfiguratorYN);
  }, [assetTypeId]);

  const handleChangeProperty = useCallback(
    curry((prop, value, popupChassisNumberValidation = false) => {
      if (prop === 'partner.dealer' || prop === 'partner.supplier') {
        actions.changeAssetProperty(
          prop === 'partner.dealer' ? `${prop}.dealerId` : `${prop}.supplierId`,
          prop === 'partner.dealer' ? value?.dealerId : value?.supplierId,
          editRight,
          popupChassisNumberValidation,
          isFacilityFrame,
          false
        );
        actions.changeAssetProperty(`${prop}.contactInfo`, null, editRight, popupChassisNumberValidation, isFacilityFrame);
        actions.changeAssetProperty(`${prop}.name`, value?.name, editRight, popupChassisNumberValidation, isFacilityFrame, false);
        actions.changeAssetProperty(
          `${prop}.registrationCode`,
          value?.registrationCode,
          editRight,
          popupChassisNumberValidation,
          isFacilityFrame,
          false,
          false
        );
      } else if (prop === 'partner.dealer.contactInfo') {
        actions.changeAssetProperty(`${prop}.name`, value.name, editRight, popupChassisNumberValidation, isFacilityFrame);
        actions.changeAssetProperty(`${prop}.email`, value.email, editRight, popupChassisNumberValidation, isFacilityFrame);
        actions.changeAssetProperty(`${prop}.phone`, value.phone, editRight, popupChassisNumberValidation, isFacilityFrame);
        actions.changeAssetProperty(`${prop}.contactData`, value.contactData, editRight, popupChassisNumberValidation, isFacilityFrame);
      } else actions.changeAssetProperty(prop, value, editRight, popupChassisNumberValidation, isFacilityFrame, true);

      if (prop === 'isChassisNumberAvailable') setLocalChassisNumber('');
      if (prop === 'chassisNumber') setLocalChassisNumber(value);
      if (prop === 'orderNumber') setLocalOrderNumber(value);
    }),
    [actions, editRight, isFacilityFrame]
  );

  const handleAssetData = value => {
    if (value && isChassisNumber().func(value)) {
      actions.loadAssetData(value, null);
    }
  };

  useEffect(() => {
    let timer = null;
    if (localOrderNumber) {
      timer = setTimeout(() => actions.loadAssetData(null, localOrderNumber), 1000);
    }
    if (timer) {
      return () => {
        clearTimeout(timer);
      };
    }
  }, [localOrderNumber]);

  const handleSave = () => {
    actions.saveAsset(onSaveAssetComplete, isFacilityFrame, editRight);
  };

  const handleDuplicate = () => {
    actions.duplicateAsset(onDuplicateComplete, isFacilityFrame, editRight);
  };

  const handleChassisNumberSave = () => {
    actions.chassisNumberSave(documentId, siteId, asset.document.documentDetailId, asset.document.chassisNumber, editRight);
  };

  const splitAsset = quantity => {
    actions.splitAsset(documentId, siteId, documentDetailId, quantity, editRight);
  };

  const handleCancelChassisNumberSave = initialChassisNumber => {
    actions.cancelChassisNumberSave(initialChassisNumber);
  };

  const loadPropertyDetails = propId => {
    actions.loadPropertyDetails(propId);
  };

  const loadPartnerContactInfo = curry((role, input) => actions.loadContactInfo(input, role));

  const { loadDealers, loadSuppliers } = actions;

  const openConfigurator = () => {
    setShowConfigurator(true);
  };

  const closeConfigurator = () => {
    setShowConfigurator(false);
  };

  const openPropertiesHistory = () => {
    setShowPropertiesHistory(true);
  };
  const closePropertiesHistory = () => {
    setShowPropertiesHistory(false);
  };

  return (
    <OfferAssetCard
      asset={asset}
      hierarchy={hierarchy}
      updateAssetChassisNo={updateAssetChassisNo}
      financedAssetWrite={financedAssetWrite}
      onPropertyChange={handleChangeProperty}
      assetImage={assetImage}
      assetIsLoaded={assetIsLoaded}
      assetPropertiesAreLoaded={assetPropertiesAreLoaded}
      currencies={currencies}
      assetTypes={assetTypes}
      onSave={handleSave}
      onDuplicate={handleDuplicate}
      updatingChassisNumber={updatingChassisNumber}
      retrievingAssetData={retrievingAssetData}
      saving={saving}
      duplicating={duplicating}
      handleChassisNumberSave={handleChassisNumberSave}
      handleCancelChassisNumberSave={handleCancelChassisNumberSave}
      onAssetDataChange={handleAssetData}
      onCancel={onAssetCanceled}
      loadPropertyDetails={loadPropertyDetails}
      loadPropertiesHistory={loadPropertiesHistory}
      loadDealers={loadDealers}
      loadSuppliers={loadSuppliers}
      loadPartnerContactInfo={loadPartnerContactInfo}
      isSearchingRestrictedDealer={userInfo.isLoaded && userInfo.userExtraInfoModel.isSearchingRestrictedDealer}
      localChassisNumber={localChassisNumber || ''}
      localSupplier={localSupplier}
      openOptions={openOptions}
      handleOpenOptions={handleOpenOptions}
      handleCloseOptions={handleCloseOptions}
      closeConfigurator={closeConfigurator}
      openConfigurator={openConfigurator}
      showConfigurator={showConfigurator}
      openPropertiesHistory={openPropertiesHistory}
      closePropertiesHistory={closePropertiesHistory}
      showPropertiesHistory={showPropertiesHistory}
      maxMonths={maxMonths}
      selfPartnerName={selfPartnerName}
      enableConfiguratorButton={enableConfiguratorButton}
      editRight={editRight}
      splitAsset={splitAsset}
      processing={processing}
      hasWorkingHours={hasWorkingHours}
      defaultValue={defaultValue}
    />
  );
};

OfferAssetContainer.propTypes = {
  documentId: PropTypes.number,
  documentDetailId: PropTypes.number,
  siteId: PropTypes.number,
  asset: PropTypes.object,
  hierarchy: PropTypes.object,
  assetIsLoaded: PropTypes.bool.isRequired,
  assetPropertiesAreLoaded: PropTypes.bool.isRequired,
  assetImage: PropTypes.string,
  currencies: PropTypes.array.isRequired,
  assetTypes: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  duplicating: PropTypes.bool.isRequired,
  history: PropTypes.object,
  steps: PropTypes.array,
  activeStep: PropTypes.number,
  userInfo: PropTypes.object.isRequired,
  maxMonths: PropTypes.number,
  rights: PropTypes.object.isRequired,
  selfPartnerName: PropTypes.string,
  isFacilityFrame: PropTypes.bool,
  params: PropTypes.object.isRequired,
  updatingChassisNumber: PropTypes.bool,
  retrievingAssetData: PropTypes.bool,
  processing: PropTypes.bool,
  hasWorkingHours: PropTypes.bool,
  defaultValue: PropTypes.string
};

const getAssetInEdit = state => {
  return state.currentOffer.assetData.inEdit;
};

const getAssetTypes = state => {
  return state.assetData.assetTypes;
};

const getAssetPropertiesAreLoaded = (state, itemId) => {
  const assetTypeProperties = Boolean(state.assetData.properties?.byTypeId[itemId]?.loading);
  return !assetTypeProperties;
};

const getAssetHierarchy = state => {
  const assetInEdit = getAssetInEdit(state);
  return assetInEdit?.asset?.document?.hierarchy;
};

function mapStateToProps(state) {
  const assetInEdit = getAssetInEdit(state);
  const assetTypes = getAssetTypes(state);
  const assetTypeId = assetInEdit?.asset?.document?.assetTypeId;
  const itemId =
    assetInEdit?.asset?.document?.itemId ??
    (assetInEdit?.asset?.document?.assetTypeId
      ? assetTypes.find(x => x.assetTypeId === assetInEdit.asset.document.assetTypeId)?.itemId
      : null);
  const assetPropertiesAreLoaded = getAssetPropertiesAreLoaded(state, itemId);
  const assetImage =
    getAssetHierarchyImage(getAssetHierarchy(state), state.assetData.images) ??
    (assetTypeId ? getAssetTypeImage(state, assetTypeId) : null);
  const { isLoaded, asset, saving, duplicating, updatingChassisNumber, retrievingAssetData, hierarchy, secondHandProperties } = assetInEdit;

  return {
    asset,
    assetIsLoaded: isLoaded,
    assetPropertiesAreLoaded,
    assetTypes,
    assetImage,
    saving,
    duplicating,
    currencies: getCurrencies(state),
    userInfo: state.userInfo,
    updatingChassisNumber,
    retrievingAssetData,
    maxMonths: state.params.items.defaultFabricationMaxMonths,
    selfPartnerName: state.offerConfig.selfPartnerName,
    assetInEdit,
    hierarchy,
    processing: state.currentOffer.header.processing,
    hasWorkingHours: secondHandProperties[itemId]?.hasWorkingHours,
    defaultValue: secondHandProperties[itemId]?.defaultValue
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        initAssetData,
        loadAssetData,
        changeAssetProperty,
        cancelAssetChanges,
        saveAsset,
        duplicateAsset,
        loadDealers,
        loadSuppliers,
        loadContactInfo,
        loadPropertyDetails,
        loadPropertiesHistory,
        chassisNumberSave,
        cancelChassisNumberSave,
        splitAsset,
        invalidateConfiguratorData,
        loadSecondHandProperties
      },
      dispatch
    )
  };
}
export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withCustomComponent('OfferAssetContainer')
)(withParams(withRights(OfferAssetContainer)));
